import { Sparkles, Quote } from "lucide-react";
import { useDailyWish } from "@/hooks/useDailyWish";

// Seasonal particle systems around the glyph — picked by Vidura per festival (effect key).
const PARTICLES = {
  fireworks: { n: 9, cls: "fx-firework", colors: ["#F5C542", "#FF8A3D", "#C4F02A", "#ff5fa2", "#7dd3fc"] },
  snow: { n: 10, cls: "fx-snow", colors: ["#ffffff", "#e0f2fe", "#f8fafc"] },
  colors: { n: 9, cls: "fx-color", colors: ["#ff4fa3", "#F5C542", "#22c55e", "#38bdf8", "#a855f7", "#FF8A3D"] },
  petals: { n: 8, cls: "fx-petal", colors: ["#FF8A3D", "#F5C542", "#fb7185", "#fbbf24"] },
  kites: { n: 5, cls: "fx-kite", colors: ["#FF8A3D", "#22c55e", "#38bdf8", "#F5C542"] },
  tricolor: { n: 9, cls: "fx-spark", colors: ["#FF9933", "#ffffff", "#138808"] },
  stars: { n: 8, cls: "fx-star", colors: ["#F5C542", "#fff7d6", "#C4F02A"] },
  lights: { n: 8, cls: "fx-light", colors: ["#F5C542", "#FF8A3D", "#C4F02A", "#fff1b8"] },
  glitter: { n: 12, cls: "fx-glitter", colors: ["#fff7d6", "#F5C542", "#ffffff", "#C4F02A", "#ffd1f0"] },
  leaves: { n: 7, cls: "fx-leaf", colors: ["#22c55e", "#C4F02A", "#86efac"] },
};

const seeded = (i, n) => {
  const a = (i * 137.508) % 360;
  const r = 22 + ((i * 53) % 12);
  return { left: `${50 + r * Math.cos((a * Math.PI) / 180)}%`, top: `${50 + r * Math.sin((a * Math.PI) / 180)}%`, delay: `${(i / n) * 3.2}s`, size: 3 + (i % 3) };
};

export const FestiveParticles = ({ effect = "lights", className = "" }) => {
  const p = PARTICLES[effect] || PARTICLES.lights;
  return (
    <span className={`absolute inset-0 pointer-events-none ${className}`} aria-hidden="true" data-testid={`festive-fx-${effect}`}>
      {Array.from({ length: p.n }).map((_, i) => {
        const s = seeded(i, p.n);
        return <span key={i} className={`absolute rounded-full ${p.cls}`}
          style={{ left: s.left, top: s.top, width: s.size, height: s.size, background: p.colors[i % p.colors.length], animationDelay: s.delay, color: p.colors[i % p.colors.length] }} />;
      })}
    </span>
  );
};

export const FestiveLogoMark = () => {
  const wish = useDailyWish();
  if (!wish || wish.kind !== "festival" || !wish.motif_url) return null;
  return (
    <span className="relative inline-flex items-center justify-center w-14 h-14 -my-2 mr-1 shrink-0 group/fest" data-testid="festive-logo-motif" aria-label={wish.wish_en}>
      <FestiveParticles effect={wish.effect} className="-inset-2" />
      <img src={wish.motif_url} alt={`${wish.festival_name || wish.title || "Festive"} motif — Growthifye`} data-testid="festive-rainbow-halo"
        className="absolute inset-0 w-full h-full object-contain fest-aura pointer-events-none" />
      <img src={wish.motif_url} alt={`${wish.festival_name} glyph`}
        className="relative w-12 h-12 object-contain transition-transform duration-700 ease-out group-hover/fest:scale-110" />
      <span className="absolute left-0 top-full mt-2 w-[300px] opacity-0 translate-y-1 pointer-events-none group-hover/fest:opacity-100 group-hover/fest:translate-y-0 transition-[opacity,transform] duration-300 z-50"
        data-testid="festive-logo-popover">
        <span className="block bg-gf-surface/95 backdrop-blur-xl border border-gf-primary/40 p-3 shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
          <span className="block font-mono text-[8px] uppercase tracking-[0.2em] text-gf-primary">{wish.is_eve ? "Tomorrow" : "Today"} · {wish.festival_name}</span>
          <span className="block font-body text-xs text-gf-text mt-1 leading-snug">{wish.wish_en}</span>
          {wish.wish_hi && <span className="block font-body text-[11px] text-gf-muted mt-1" lang="hi">{wish.wish_hi}</span>}
        </span>
      </span>
    </span>
  );
};

export const FestiveTopLine = () => {
  const wish = useDailyWish();
  if (!wish || wish.kind !== "festival") return null;
  return <span className="absolute inset-x-0 top-0 h-[2px] fest-topline pointer-events-none" aria-hidden="true" data-testid="festive-topline" />;
};

export const LogoWishLine = ({ lang = "en" }) => {
  const wish = useDailyWish();
  if (!wish) return null;
  const festive = wish.kind === "festival";
  const primary = festive ? (lang === "hi" && wish.wish_hi ? wish.wish_hi : wish.wish_en) : `“${wish.quote?.text}”`;
  const secondary = festive ? (lang === "hi" ? wish.wish_en : wish.wish_hi) : (wish.quote?.author && `— ${wish.quote.author}`);
  return (
    <div className={`hidden 2xl:flex items-center gap-2 max-w-[230px] pl-3 border-l ${festive ? "border-gf-primary/40" : "border-white/10"} shrink min-w-0`}
      data-testid="logo-wish-line" title={wish.wish_en}>
      {festive
        ? <Sparkles className="w-3.5 h-3.5 text-gf-primary shrink-0 animate-pulse" />
        : <Quote className="w-3 h-3 text-gf-muted shrink-0" />}
      <div className="min-w-0 leading-tight">
        <p className={`font-body text-[11px] truncate ${festive ? "text-gf-primary" : "text-gf-text/85 italic"}`}>{primary}</p>
        {secondary && <p className={`font-body text-[10px] text-gf-muted truncate ${!festive ? "font-mono text-[9px] uppercase tracking-wider" : ""}`}>{secondary}</p>}
      </div>
    </div>
  );
};
